import { memo, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  ArrowLeftRight,
  BookOpen,
  Bug,
  ChevronLeft,
  ChevronRight,
  Database,
  Droplet,
  Home,
  Layers,
  LineChart,
  type LucideIcon,
  ListChecks,
  BellOff,
  Percent,
  Radar,
  Send,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Logo } from "./Logo";
import { api } from "@/lib/api";
import { C } from "@/lib/colors";
import { useConnectedVenues } from "@/lib/useTopBarStats";
import { selectUser, useAuth } from "../stores/auth";
import { useUiStore } from "../stores/ui";
import { useMediaQuery } from "../lib/useMediaQuery";

/** Sidebar nav entry. ``icon`` is the lucide component (rendered at
 *  16 × 16 px both expanded and collapsed). ``adminOnly`` filters out
 *  for non-admin users — purely cosmetic, the page itself is also
 *  guarded by ``<AdminRoute>`` and the backend by ``Depends(require_admin)``.
 *  ``external: true`` switches the rendering from a react-router
 *  ``<NavLink>`` (in-app navigation) to a plain ``<a target="_blank">``
 *  (off-site link). Used by the "Signals" entry that deeplinks into
 *  the welcome Telegram bot. */
type NavEntry = {
  to: string;
  icon: LucideIcon;
  label: string;
  end?: boolean;
  adminOnly?: boolean;
  external?: boolean;
};

const PUBLIC_NAV: readonly NavEntry[] = [
  { to: "/", icon: Home, label: "Dashboard", end: true },
  { to: "/spreads", icon: TrendingUp, label: "Spreads" },
  // Premarket added 2026-05-23 — promotes the E6-E8 pre-IPO /
  // tokenised-equity flow to a first-class nav slot (was previously
  // only reachable via the Telegram CHANNEL_PREMARKET subscription).
  // ``Radar`` icon picks up the "scanning for asymmetric situations"
  // metaphor without colliding visually with the Spreads ↗ icon.
  { to: "/premarket", icon: Radar, label: "Premarket" },
  { to: "/funding", icon: Percent, label: "Funding" },
  // Charts hub (#68) — funding-rate-per-exchange + spread/price charts for a
  // token. ``LineChart`` reads as a multi-series time chart, distinct from the
  // Spreads ↗ trend and Funding % glyphs above.
  { to: "/charts", icon: LineChart, label: "Charts" },
  { to: "/dex-dump", icon: Droplet, label: "Dex Dump" },
  // CEX pump/dump feed (#12) — short-window (1-30s) CEX price moves.
  // ``Activity`` icon reads as a live ticker/heartbeat, distinct from
  // Spreads' ↗ trend and the Droplet DEX-dump glyph above.
  { to: "/cex-dump", icon: Activity, label: "Pump/Dump" },
  { to: "/walls", icon: Layers, label: "Walls" },
  { to: "/listings", icon: Sparkles, label: "Listings" },
  { to: "/dw-changes", icon: ArrowLeftRight, label: "D/W Changes" },
  // Token Info moved to a top-right TopBar button → right-side drawer (#105).
];

const ADMIN_NAV: readonly NavEntry[] = [
  { to: "/orderbooks", icon: BookOpen, label: "Orderbooks", adminOnly: true },
  { to: "/tracker", icon: ListChecks, label: "Tracker", adminOnly: true },
  { to: "/mutes", icon: BellOff, label: "Mutes", adminOnly: true },
  { to: "/debug", icon: Bug, label: "Debug", adminOnly: true },
  { to: "/tax-cache", icon: Database, label: "Tax Cache", adminOnly: true },
];

// 240 → 180 px (2026-05-02). Even the longest label ("Orderbooks" /
// "Token Info" — 10 chars) sits comfortably inside 180 with its 16 px
// icon + 12 px gap. Earlier 240 left a noticeable empty strip on the
// right of every nav row.
const SIDEBAR_W_EXPANDED = 180;
const SIDEBAR_W_COLLAPSED = 60;
const MOBILE_BREAKPOINT = "(max-width: 767px)";

/**
 * Vertical sidebar navigation. Three modes:
 *
 *   * desktop expanded   — 240 px wide, icons + labels + section headers
 *   * desktop collapsed  — 60 px wide, icons only with browser-tooltip on hover
 *   * mobile drawer      — full-height overlay, slides in from the left
 *
 * Toggle button on the right edge floats half-overlapping the sidebar
 * border (Discord/Slack pattern) so it stays accessible regardless of
 * collapse state. On mobile the drawer is opened from a hamburger
 * button in ``TopBar``; this component closes itself automatically on
 * any nav-link click.
 */
export const Sidebar = memo(function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const mobileOpen = useUiStore((s) => s.mobileSidebarOpen);
  const setMobileOpen = useUiStore((s) => s.setMobileSidebar);
  const user = useAuth(selectUser);
  const isAdmin = user?.role === "admin";
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const location = useLocation();

  // Welcome-bot username comes from ``/api/config`` — same env var the
  // marketing landing reads (``WELCOME_BOT_USERNAME``). One fetch on
  // mount, no polling; empty value means no Signals entry is shown.
  const [botUsername, setBotUsername] = useState<string>("");
  useEffect(() => {
    api
      .get<{ welcome_bot_username?: string }>("/api/config")
      .then((c) => setBotUsername(c.welcome_bot_username || ""))
      .catch(() => {
        // Silent — the entry just won't render. Better than blanking
        // the sidebar on a transient API blip.
      });
  }, []);

  // Public nav with the Signals deeplink appended when configured.
  // ``?start=trial`` is the same payload the landing CTA carries —
  // bot's /start handler treats both paths identically.
  const publicNav: readonly NavEntry[] = botUsername
    ? [
        ...PUBLIC_NAV,
        {
          to: `https://t.me/${botUsername}?start=trial`,
          icon: Send,
          label: "Signals",
          external: true,
        },
      ]
    : PUBLIC_NAV;

  // Auto-close mobile drawer whenever the route changes — nav-link
  // taps shouldn't leave the overlay covering the page they're trying
  // to read.
  useEffect(() => {
    if (isMobile && mobileOpen) setMobileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isMobile]);

  // Close on Esc — standard expectation for any modal-ish overlay.
  useEffect(() => {
    if (!isMobile || !mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile, mobileOpen, setMobileOpen]);

  // Effective width drives both the sidebar element AND the spacer
  // ``<div>`` inserted by ``Layout`` so the main content shifts with
  // collapse on desktop. On mobile the sidebar is overlay (position:
  // fixed) so the spacer is 0 — main content uses the full viewport.
  const width = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W_EXPANDED;
  const visible = !isMobile || mobileOpen;

  // Icons-only "collapsed" mode is a desktop concept. On mobile we
  // always render the expanded layout inside the overlay because
  // touch users expect labels (no hover-tooltip).
  const iconsOnly = collapsed && !isMobile;

  return (
    <>
      {/* Backdrop on mobile while drawer is open. */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          aria-hidden="true"
        />
      )}

      <aside
        className="flex flex-col"
        style={{
          position: isMobile ? "fixed" : "relative",
          top: 0,
          left: 0,
          height: "100%",
          width: isMobile ? SIDEBAR_W_EXPANDED : width,
          background: C.bg,
          borderRight: `1px solid ${C.border}`,
          zIndex: 50,
          transition: "width 150ms ease-out, transform 150ms ease-out",
          transform: visible ? "translateX(0)" : `translateX(-100%)`,
          flexShrink: 0,
        }}
      >
        {/* Header — logo + brand (2026-05-23 brand sync).
            Hybrid brand: "Finder" stays as the primary name (matches
            bot, telegram channels, social), "arb · terminal" as a
            product subtitle that mirrors the landing's sidebar mock.
            Collapsed mode shows only the logo. */}
        <div
          className="flex items-center"
          style={{
            height: 56,
            padding: iconsOnly ? "0 14px" : "0 16px",
            borderBottom: `1px solid ${C.border}`,
            gap: 10,
          }}
        >
          <Logo size={22} />
          {!iconsOnly && (
            <div className="min-w-0">
              <div
                className="font-mono"
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.text1,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  lineHeight: 1.1,
                }}
              >
                Finder
              </div>
              <div
                className="font-mono"
                style={{
                  fontSize: 9,
                  color: C.text4,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  marginTop: 2,
                  lineHeight: 1,
                }}
              >
                arb · terminal
              </div>
            </div>
          )}
        </div>

        {/* Nav body — scrollable section list. */}
        <div className="flex-1 min-h-0 overflow-y-auto" style={{ padding: "8px 0" }}>
          <NavSection
            label="Public"
            items={publicNav}
            iconsOnly={iconsOnly}
            isAdmin={isAdmin}
          />
          <div
            style={{
              height: 1,
              margin: "8px 12px",
              background: C.border,
            }}
          />
          <NavSection
            label="Admin"
            items={ADMIN_NAV}
            iconsOnly={iconsOnly}
            isAdmin={isAdmin}
          />
        </div>

        {/* Sidebar foot — brand-sync addition (2026-05-23).
            Two lines: a "Connected · N venues" live status (mint dot
            mirrors the landing's hero ticker pulse), and a Back-to-
            site link that lets the operator return to the public
            landing without manually editing the URL. Collapsed mode
            hides both — the icons-only sidebar saves them for the
            expanded state where they're readable. */}
        {!iconsOnly && (
          <div
            style={{
              borderTop: `1px solid ${C.border}`,
              padding: "10px 16px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 4,
              flexShrink: 0,
            }}
          >
            <ConnectedStatus />
            <a
              href="/"
              className="font-mono transition-colors"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 0",
                fontSize: 11,
                color: C.text3,
                letterSpacing: "0.04em",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = C.cy;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = C.text3;
              }}
            >
              <ArrowLeft size={12} strokeWidth={2} />
              <span>Back to site</span>
            </a>
            {/* pulse keyframe centralised in index.css (plan 001); 1.6s
                cadence keeps the foot "calmly alive" rather than urgent. */}
          </div>
        )}
      </aside>

      {/* Floating collapse toggle on the right edge — Discord/Slack
          pattern. Half-overlaps the sidebar border so it's reachable
          in either expanded or collapsed state. Hidden on mobile (the
          drawer doesn't have a "collapsed" mode — it's open or hidden).
          The button moves with the sidebar border via ``left:`` set to
          the current width. */}
      {!isMobile && (
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand" : "Collapse"}
          className="cursor-pointer transition-colors"
          style={{
            position: "fixed",
            top: 28,                // vertically aligned with sidebar header
            left: width - 12,       // half-overlap the border
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: C.raised,
            border: `1px solid ${C.borderHi}`,
            color: C.text3,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 60,
            transition: "left 150ms ease-out, color 120ms",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = C.text1;
            e.currentTarget.style.background = C.surfaceHi;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = C.text3;
            e.currentTarget.style.background = C.raised;
          }}
        >
          {collapsed ? <ChevronRight size={14} strokeWidth={2.5} /> : <ChevronLeft size={14} strokeWidth={2.5} />}
        </button>
      )}
    </>
  );
});

/** Live venue counter for the sidebar foot. Replaces a hard-coded
 *  "12 exchanges" with the distinct count of CEX venues actually present
 *  in the live ``/ws/spreads`` snapshot. Its own component so only this
 *  line re-renders per snapshot — the memoised Sidebar stays put. When
 *  the websocket is down (or no snapshot yet) it degrades the dot/label
 *  honestly instead of pulsing a fake "Connected". */
function ConnectedStatus() {
  const { count, connected } = useConnectedVenues();
  const live = connected && count > 0;
  const label = live
    ? `Connected · ${count} exchanges`
    : connected
      ? "Connecting…"
      : "Offline";
  return (
    <div
      className="font-mono"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 10,
        color: C.text4,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        padding: "4px 0",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: live ? C.mint : C.text4,
          boxShadow: live ? `0 0 8px ${C.mint}` : "none",
          animation: live ? "pulse 1.6s ease-in-out infinite" : "none",
          flexShrink: 0,
        }}
        aria-hidden="true"
      />
      <span>{label}</span>
    </div>
  );
}

interface SectionProps {
  label: string;
  items: readonly NavEntry[];
  iconsOnly: boolean;
  isAdmin: boolean;
}

function NavSection({ label, items, iconsOnly, isAdmin }: SectionProps) {
  // Filter admin-only items for non-admin users. Returning an empty
  // section (the parent renders the divider regardless) is fine — it
  // collapses to 0 height visually.
  const visibleItems = items.filter((it) => !it.adminOnly || isAdmin);
  if (visibleItems.length === 0) return null;

  return (
    <div>
      {!iconsOnly && (
        <div
          className="font-mono uppercase"
          style={{
            padding: "6px 16px 4px",
            fontSize: 9,
            letterSpacing: "0.14em",
            color: C.text4,
            fontWeight: 600,
          }}
        >
          {label}
        </div>
      )}
      {visibleItems.map((it) => (
        <NavItem key={it.to} entry={it} iconsOnly={iconsOnly} />
      ))}
    </div>
  );
}

function NavItem({ entry, iconsOnly }: { entry: NavEntry; iconsOnly: boolean }) {
  const Icon = entry.icon;
  // Shared style + visual atoms so external <a> and react-router NavLink
  // look identical in the sidebar. Same hover state, same active-state
  // contrast — but external never gets the active state because it's a
  // different host entirely.
  const baseStyle: React.CSSProperties = {
    gap: 12,
    padding: iconsOnly ? "10px 0" : "9px 16px",
    margin: "1px 8px",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 500,
    justifyContent: iconsOnly ? "center" : "flex-start",
    // ``min-w-0`` prevents long labels from forcing the sidebar
    // width past 240 px during the brief moment when the wrong
    // collapsed-state value is in flight.
    minWidth: 0,
    position: "relative",
  };

  // External entries: plain anchor, opens in new tab. The Telegram
  // "Signals" link is the canonical example — it gets a tg-blue
  // tinted background to flag it as a cross-product CTA (lands the
  // user on the marketing bot rather than an in-app route). Other
  // hypothetical external links would render as muted text via the
  // generic branch below.
  if (entry.external) {
    const isTelegramCTA = entry.to.includes("t.me/") || entry.label === "Signals";
    if (isTelegramCTA) {
      return (
        <a
          href={entry.to}
          target="_blank"
          rel="noopener"
          title={iconsOnly ? entry.label : undefined}
          className="flex items-center transition-colors"
          style={{
            ...baseStyle,
            background: C.tgBg,
            color: C.tg,
            border: `1px solid rgba(42,171,238,0.25)`,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(42,171,238,0.18)";
            e.currentTarget.style.borderColor = "rgba(42,171,238,0.45)";
            e.currentTarget.style.color = "#5fc1f5";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = C.tgBg;
            e.currentTarget.style.borderColor = "rgba(42,171,238,0.25)";
            e.currentTarget.style.color = C.tg;
          }}
        >
          <Icon size={16} strokeWidth={1.75} style={{ color: "inherit" }} />
          {!iconsOnly && <span className="truncate">{entry.label}</span>}
        </a>
      );
    }
    return (
      <a
        href={entry.to}
        target="_blank"
        rel="noopener"
        title={iconsOnly ? entry.label : undefined}
        className="flex items-center text-text3 transition-colors hover:bg-raised/60 hover:text-text2"
        style={baseStyle}
      >
        <Icon size={16} strokeWidth={1.75} />
        {!iconsOnly && <span className="truncate">{entry.label}</span>}
      </a>
    );
  }

  return (
    <NavLink
      to={entry.to}
      end={entry.end ?? false}
      title={iconsOnly ? entry.label : undefined}
      className={({ isActive }) =>
        `flex items-center transition-colors ${
          isActive
            ? "bg-raised text-text1"
            : "text-text3 hover:bg-raised/60 hover:text-text2"
        }`
      }
      style={baseStyle}
    >
      {({ isActive }) => (
        <>
          {/* Cyan vertical bar — left edge accent on active nav.
              Positioned to half-overlap the 8 px outer margin so the
              bar sits cleanly inside the "rail" rather than against
              the row. Only rendered when isActive to keep the DOM
              identical for non-active rows. Brand-sync 2026-05-23. */}
          {isActive && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                left: -8,
                top: 7,
                bottom: 7,
                width: 2,
                background: C.cy,
                borderRadius: 4,
              }}
            />
          )}
          <Icon
            size={16}
            strokeWidth={1.75}
            style={{ color: isActive ? C.cy : undefined }}
          />
          {!iconsOnly && <span className="truncate">{entry.label}</span>}
        </>
      )}
    </NavLink>
  );
}
